package co.torvus.web

import io.ktor.client.HttpClient
import io.ktor.client.engine.js.Js
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import io.ktor.serialization.kotlinx.json.json
import kotlinx.coroutines.MainScope
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

/*
 * Contact form delivery via FormSubmit.co — a free form-to-email service with a free
 * autoresponder (auto-reply to the sender). No account or API key needed.
 *
 * ONE-TIME SETUP: submit the form once; FormSubmit emails info@torvus.co an activation
 * link — click it and the form goes live. After that, each submission is emailed to
 * info@torvus.co and the sender gets the [AUTORESPONSE] reply. Optionally, once activated,
 * swap CONTACT_ENDPOINT for the hashed alias FormSubmit gives you
 * (https://formsubmit.co/ajax/<random>) to keep the address out of the page source.
 */
private const val CONTACT_ENDPOINT = "https://formsubmit.co/ajax/info@torvus.co"
private const val AUTORESPONSE =
    "Thanks for reaching out to Torvus — we've received your message and will get back to you soon."

/** App-wide coroutine scope for fire-and-forget UI actions (e.g. the contact submit). */
val contactScope = MainScope()

private val contactClient = HttpClient(Js) {
    // encodeDefaults so the default-valued FormSubmit fields (_autoresponse, _template,
    // _captcha) are actually included in the JSON — otherwise the auto-reply never fires.
    install(ContentNegotiation) { json(Json { ignoreUnknownKeys = true; encodeDefaults = true }) }
}

@Serializable
private data class ContactPayload(
    val email: String,
    val message: String,
    @SerialName("_subject") val subjectLine: String,
    @SerialName("_autoresponse") val autoresponse: String = AUTORESPONSE,
    @SerialName("_template") val template: String = "table",
    @SerialName("_captcha") val captcha: String = "false",
)

/** POSTs the message to FormSubmit. Returns true if it was accepted (2xx). */
suspend fun submitContactMessage(email: String, subject: String, message: String): Boolean =
    try {
        val response = contactClient.post(CONTACT_ENDPOINT) {
            contentType(ContentType.Application.Json)
            setBody(
                ContactPayload(
                    email = email,
                    message = message,
                    subjectLine = if (subject.isBlank()) {
                        "New Torvus contact message"
                    } else {
                        "Torvus contact: $subject"
                    },
                ),
            )
        }
        response.status.isSuccess()
    } catch (e: Throwable) {
        console.error("Contact submit failed", e)
        false
    }
